import { FormikErrors, FormikTouched } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import { ALTERNATIVE_POSITIONS } from "../../models/Onboarding/onboarding-values";
import {
  Option,
  TVacancyFormValues,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_START_AVAILABILITY,
} from "../../models/vacancy";
import { AUTH_ROUTE } from "../../pages/App/routes";
import { useAppSelector, useAppSelector as useSelector } from "../../store";
import { TCenterDetails } from "../../store/superadmin/types";
import { TPosition } from "../../store/vacancy/types";
import storage from "../../utils/storage";
import CustomButton from "../UI/Buttons/CustomButton";
import LeftArrow from "../UI/Icons/LeftArrow";
import RoundedTick from "../UI/Icons/RoundedTick";
import CharacterLimitTextarea from "../UI/Inputs/CharacterLimitTextarea";
import Checkbox from "../UI/Inputs/Checkbox";
import RadioInput from "../UI/Inputs/RadioInput";
import ToggleSelector from "../UI/Inputs/ToggleSelector";
import CustomSelect from "../UI/Select";
import TimeframeSelector from "../UI/TimeframeSelector";
import ErrorMessage from "../UI/Tooltips/ErrorMessage";

const alternativePositions = [  
  ALTERNATIVE_POSITIONS.ADMIN_ASSISTANT,
  ALTERNATIVE_POSITIONS.CLEANER,
  ALTERNATIVE_POSITIONS.GARDENER,
  ALTERNATIVE_POSITIONS.CHEF_COOK
];

const vacancyStartOptions = [
  {
    label: "From now",
    value: VACANCY_START_AVAILABILITY.NOW,
    checkedAlert: true,
  },
  {
    label: "Within the next 24 hours",
    value: VACANCY_START_AVAILABILITY.WITHIN_24_HOURS,
    checkedAlert: true,
  },
  {
    label: "On a future date",
    value: VACANCY_START_AVAILABILITY.FUTURE_DATE,
    checkedAlert: false,
  },
];

const vacancyCandidateVisibilityOptions: Option[] = [
  { label: "Any Candidate", value: VACANCY_CANDIDATE_VISIBILITY.ANY_CANDIDATE },
  {
    label: "Preferred Candidates Only",
    value: VACANCY_CANDIDATE_VISIBILITY.PREFERRED_CANDIDATES,
  },
  {
    label: "One Selected Candidate Only",
    value: VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE,
  },
];

export interface IVacancyFieldsProps {
  isAdmin: boolean;
  isEdit: boolean;
  values: TVacancyFormValues; // Formik form values
  touched: FormikTouched<TVacancyFormValues>; // Formik touched fields
  errors: FormikErrors<TVacancyFormValues>; // Formik error fields
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void; // Formik's setFieldValue
  allCentreOptions: Option[];
  CandidateSelectionOptions: Option[];
  candidatePositions: TPosition[];
  allActiveCandidateOptions: Option[];
  responseTimeframes: Option[];
  allResponseTimeframes: Option[];
  disableCandidatesSelection: boolean;
  fetchActiveCandidates: () => void;
  onSubmit: () => void;
}


const VacancyFields = ({
  isAdmin,
  isEdit,
  values,
  touched,
  errors,
  setFieldValue,
  allCentreOptions,
  candidatePositions,
  allActiveCandidateOptions,
  responseTimeframes,
  allResponseTimeframes,
  CandidateSelectionOptions,
  disableCandidatesSelection,
  fetchActiveCandidates,
  onSubmit,
}: IVacancyFieldsProps) => {
  const navigate = useNavigate();
  const InProgress = useSelector((state) => state.vacancy.inProgress); // Get the inProgress state from the Redux store to manage the loading state of the button
  const allCentreListData = useAppSelector((state) => state.Centres.allCenters);
  const [selectedCentreData, setSelectedCentreData] = useState<TCenterDetails>({} as TCenterDetails);
  const prevTimeframeDatesRef = useRef<Record<string | number, string>>({});
  // Redirect to vacancy list based on user type
  const redirectToVacancies = () => {
    // Remove selected / duplicate vacancy from storage
    storage.remove(STORAGE_KEYS.SELECTED_VACANCY_ID);
    storage.remove(STORAGE_KEYS.DUPLICATE_VACANCY_ID);

    navigate(
      isAdmin ? AUTH_ROUTE.ADMIN_URGENT_VACANCIES : AUTH_ROUTE.CLIENT_VACANCIES
    );
  };

  const handleFieldChange = useCallback(
    (field: keyof TVacancyFormValues, value: any) => {
      setFieldValue(field, value);
    },
    [setFieldValue]
  );

  // Helper function to get next business day (skip weekends)
  const getNextBusinessDay = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayOfWeek = tomorrow.getDay();

    // If tomorrow is Saturday (6), add 2 days to get Monday
    if (dayOfWeek === 6) {
      tomorrow.setDate(tomorrow.getDate() + 2);
    }
    // If tomorrow is Sunday (0), add 1 day to get Monday
    else if (dayOfWeek === 0) {
      tomorrow.setDate(tomorrow.getDate() + 1);
    }

    return tomorrow;
  };

  // Accepts array of objects with 'date' as [Date | null, Date | null] | null
  const getTimeframesDatesKey = (timeframes: { date?: [Date | null, Date | null] | null }[] = []) =>
    timeframes
      .map(tf =>
        (tf.date
          ? tf.date
              .map((d) =>
                d instanceof Date
                  ? d.toISOString()
                  : d !== null
                  ? String(d)
                  : ""
              )
              .join(",")
          : ""
        )
      )
      .join("|");

  const datesKey = getTimeframesDatesKey(values.timeframes);


  const handlePositionChange = (position: TPosition, checked: boolean) => {
    const updated = checked
      ? [...values.position_uuids, position.uuid]
      : values.position_uuids.filter((id) => id !== position.uuid);

    handleFieldChange("position_uuids", updated);
    handleFieldChange("alternate_position_legacy", ""); // reset alternate
  };

  const handleSelectAllPositions = () => {
    if (areAllPositionsSelected) {
      handleFieldChange("position_uuids", []);
    } else {
      handleFieldChange(
        "position_uuids",
        candidatePositions.map((p) => p.uuid)
      );
    }
    handleFieldChange("alternate_position_legacy", ""); // clear alternate
  };

  const handleResponseTimeframeCheck = (value: boolean) => {
    handleFieldChange("response_timeframe_check", value);
    if (value === true) {
      handleFieldChange("response_timeframe", 15);
    } else {
      handleFieldChange("all_response_timeframe_check", false);
      handleFieldChange("all_response_timeframe", null);
    }
  };

  const handleResponseTimeframeChange = (value: number) => {
    handleFieldChange("response_timeframe", value);
    if (values.all_response_timeframe_check && values.all_response_timeframe !== null && (values.all_response_timeframe as number) <= value) {
      const firstValid = allResponseTimeframes.find((opt) => (opt.value as number) > value);
      handleFieldChange("all_response_timeframe", firstValid?.value ?? null);
    }
  };

  const handleAllResponseTimeframeCheck = (value: boolean) => {
    handleFieldChange("all_response_timeframe_check", value);
    if (value === true) {
      const filtered = values.response_timeframe_check
        ? allResponseTimeframes.filter(
            (opt) => (opt.value as number) > values.response_timeframe
          )
        : allResponseTimeframes;
      const defaultVal = filtered[0]?.value ?? allResponseTimeframes[0]?.value ?? 15;
      handleFieldChange("all_response_timeframe", defaultVal);
    }
  };

  const renderVacancyStartOptions = () =>
    vacancyStartOptions.map(({ label, value, checkedAlert }) => (
      <div key={uuidv4()} className="theme-check">
        <RadioInput
          label={label}
          name="timeline_type"
          value={value}
          checked={values.timeline_type === value}
          onChange={() => handleFieldChange("timeline_type", value)}
          themeCheck={true}
          checkedAlert={checkedAlert}
        />
      </div>
    ));

  const renderCandidatePositionCheckboxes = () =>
    candidatePositions.map((position) => (
      <div className="theme-check" key={position.uuid}>
        <Checkbox
          key={position.uuid}
          checked={values.position_uuids.includes(position.uuid)}
          label={position.name}
          // disabled={!!values.alternate_position_legacy} // disable if alternate is selected
          onChange={(checked) => handlePositionChange(position, checked)}
          themeCheck={true}
          id={position.uuid}
        />
      </div>
    ));

  const renderAlternativePositionRadios = () =>
    alternativePositions.map((alt) => (
      <div key={uuidv4()} className="theme-check">
        <RadioInput
          label={alt}
          name="alternate_position_legacy"
          value={alt}
          checked={values.alternate_position_legacy === alt}
          onChange={() => {
            handleFieldChange("alternate_position_legacy", alt);
            handleFieldChange("position_uuids", []);
          }}
          themeCheck={true}
        />
      </div>
    ));

  const areAllPositionsSelected = candidatePositions.every((position) =>
    values.position_uuids.includes(position.uuid)
  );

  const handleChange = (data: string) => {
    handleFieldChange("shift_notes", data);
  };

  useEffect(() => {
    const centreDetails = allCentreListData.find((item) => item.id === values.centre_id);
    setSelectedCentreData(centreDetails || {} as TCenterDetails);
  }, [values.centre_id]);

  const updateTimeFrameTimes = (selectedTimeFrameId: number | string, newStartTime: string, newEndTime: string) => {
    const updatedTimeframes = (values.timeframes || []).map(tf =>
      tf.id === selectedTimeFrameId
        ? { ...tf, start_time: newStartTime, end_time: newEndTime }
        : tf
    );

    // Only update if the timeframes have actually changed
    const isChanged = (values.timeframes || []).some(tf =>
      tf.id === selectedTimeFrameId &&
      (tf.start_time !== newStartTime || tf.end_time !== newEndTime)
    );

    if (isChanged) {
      handleFieldChange("timeframes", updatedTimeframes);
    }
  };

  const handleCentreStartEndTime = () => {
    const selectedTimeframeDates = values.timeframes;
    if (!selectedTimeframeDates) return;

    selectedTimeframeDates.forEach(timeFrames => {
        // Only update times for this timeframe if its dates actually changed
        const tfDateKey = getTimeframesDatesKey([timeFrames]);
        const prevKey = prevTimeframeDatesRef.current[timeFrames.id];
        if (tfDateKey === prevKey) return;

        let centreStartTime:any = null;
        let centreEndTime:any = null;
        const selectedStartDate = timeFrames?.date?.[0];
        const selectedEndDate = timeFrames?.date?.[1];
        const selectedTimeFrameId = timeFrames?.id;
        if (
          selectedStartDate &&
          selectedStartDate !== null &&
          selectedStartDate !== undefined &&
          selectedStartDate !== selectedEndDate // Ensure start and end dates are not the same
        ) {
          const startDate = new Date(selectedStartDate);
          centreStartTime =
            selectedCentreData?.timings?.filter(
              (timings) =>
          timings.day ===
          startDate.toLocaleString("en-US", { weekday: "long" })
            )?.[0]?.start_time;
        }

        if (
          selectedEndDate &&
          selectedEndDate !== null &&
          selectedEndDate !== undefined &&
          selectedStartDate !== selectedEndDate // Ensure start and end dates are not the same
        ) {
          const endDate = new Date(selectedEndDate);
          centreEndTime =
            selectedCentreData?.timings?.filter(
              (timings) =>
                timings.day ===
                endDate.toLocaleString("en-US", { weekday: "long" })
            )?.[0]?.end_time;
        }
        if(centreStartTime && centreEndTime) {
          updateTimeFrameTimes(selectedTimeFrameId, centreStartTime, centreEndTime);
        }else{
          if(selectedStartDate){
            const startDate = new Date(selectedStartDate);
            const centreTimings = selectedCentreData?.timings?.filter(
              (timings) => timings.day === startDate.toLocaleString("en-US", { weekday: "long" })
            )?.[0];
            const currentStartTime = centreTimings && centreTimings.start_time;
            const currentEndTime = centreTimings && centreTimings.end_time;
            if(currentStartTime && currentEndTime){
              updateTimeFrameTimes(selectedTimeFrameId, currentStartTime, currentEndTime);
            } else {
              // Centre has no hours for this day — use default 8AM–5PM
              updateTimeFrameTimes(selectedTimeFrameId, "08:00", "17:00");
            }
          }
        }
    });

    // Update ref with the current date keys so subsequent renders can diff
    const newKeys: Record<string | number, string> = {};
    selectedTimeframeDates.forEach(tf => {
      newKeys[tf.id] = getTimeframesDatesKey([tf]);
    });
    prevTimeframeDatesRef.current = newKeys;
  }

  useEffect(() => {
    if (
      selectedCentreData &&
      Object.keys(selectedCentreData).length > 0 &&
      values.timeline_type !== "" &&
      !isEdit
    ) {
      // for click up - 86czv91jk
      handleCentreStartEndTime(); 
    }
  }, [
    selectedCentreData,
    values.timeline_type,
    datesKey,
    isEdit,
    handleFieldChange,
  ]);

  // When "On a future date" is selected, set the timeframe to the next business day
  useEffect(() => {
    if (
      values.timeline_type === VACANCY_START_AVAILABILITY.FUTURE_DATE &&
      values.timeframes?.length === 1 &&
      !values.timeframes[0]?.date?.[1] // Only set if end date is not already set
    ) {
      const nextBusinessDay = getNextBusinessDay();
      const newTimeframe = {
        id: uuidv4(),
        date: [nextBusinessDay, nextBusinessDay] as [Date | null, Date | null], // Set both start and end to same day
        start_time: "08:00",
        end_time: "17:00",
      };
      handleFieldChange("timeframes", [newTimeframe]);
    }
  }, [values.timeline_type]);

  const filteredAllResponseTimeframes = values.response_timeframe_check
    ? allResponseTimeframes.filter(
        (opt) => (opt.value as number) > values.response_timeframe
      )
    : allResponseTimeframes;

  return (
    <div className="d-flex gap-60 gap-mobile-10 flex-column flex-lg-row">
      <div className="d-flex flex-column gap-20 w-100">
        <div className="d-flex">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={redirectToVacancies}
          >
            <LeftArrow />
            Back to Vacancies
          </CustomButton>
        </div>
        <div className="fs-30 fw-400 text-black">
          {isEdit ? "Edit" : "Create"} a Vacancy
        </div>
        {allCentreOptions.length > 1 ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">Select a centre:</div>
            <div className="form-group">
              <CustomSelect
                options={allCentreOptions}
                placeholder="Select..."
                onSelect={(opt) => handleFieldChange("centre_id", opt.value)}
                defaultSelected={allCentreOptions.find(
                  (item) => item.value === values.centre_id
                )}
                enableFilter={true}
              />
            </div>
            {errors?.centre_id && <ErrorMessage message={errors?.centre_id} />}
          </div>
        ) : null}
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            This vacancy requires a candidate:
          </div>
          {renderVacancyStartOptions()}
          {errors.timeline_type && (
            <ErrorMessage message={errors.timeline_type} />
          )}
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            {values.timeline_type === VACANCY_START_AVAILABILITY.NOW
              ? "Select an end date and time:"
              : "Select a single date, multiple dates, or multiple date ranges:"}
          </div>
          <TimeframeSelector
            isEdit={isEdit}
            startAvailability={
              values.timeline_type as VACANCY_START_AVAILABILITY
            }
            value={values.timeframes}
            onChange={(val) => {
              handleFieldChange("timeframes", val);
            }}
            error={
              touched.timeframes && (errors.timeframes as string | string[])
            }
          />
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            Include shift notes to share with the successful candidate:
          </div>
          <CharacterLimitTextarea
            maxLength={300}
            placeholder="Parking instructions, person reporting to on arrival etc."
            value={values?.shift_notes}
            onChange={(value) => handleChange(value)}
            error={
              typeof errors.shift_notes === "string"
                ? errors.shift_notes
                : undefined
            }
          />
        </div>
      </div>
      <div className="w-1px bg-grey-light"></div>
      <div className="d-flex flex-column gap-30 w-100">
        <div className="d-flex flex-column gap-10">
          <div className="d-flex align-items-center gap-10 flex-wrap">
            <div className="fs-16 fw-400 text-black">
              Select acceptable candidate level/s:
            </div>
            <div
              className="fs-13 fw-700 text-black text-decoration-underline cursor-pointer"
              onClick={handleSelectAllPositions}
            >
              {areAllPositionsSelected ? "Unselect All" : "Select All"}
            </div>
          </div>
          {renderCandidatePositionCheckboxes()}
          {errors?.position_uuids && (
            <ErrorMessage message={errors.position_uuids as string} />
          )}
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            <span className="fw-700">OR</span> Select one alternative position:
          </div>
          {renderAlternativePositionRadios()}
          {errors?.alternate_position_legacy && (
            <ErrorMessage message={errors.alternate_position_legacy} />
          )}
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            Who can view and accept this vacancy?
          </div>
          <CustomSelect
            options={vacancyCandidateVisibilityOptions}
            placeholder="Select..."
            onSelect={(opt) => handleFieldChange("visibility_type", opt.value)}
            enableFilter={false}
            defaultSelected={vacancyCandidateVisibilityOptions.find(
              (item) => item.value === values.visibility_type
            )}
          />
          {errors?.visibility_type && (
            <ErrorMessage message={errors?.visibility_type} />
          )}
          {values.visibility_type ===
            VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE && (
            <CustomSelect
              options={allActiveCandidateOptions}
              placeholder="Select from available candidates..."
              onSelect={(opt) =>
                handleFieldChange("selected_candidate_id", opt.value)
              }
              enableFilter={true}
              defaultSelected={allActiveCandidateOptions.find(
                (item) => item.value === values.selected_candidate_id
              )}
              showStarRating={true}
              // disabled={disableCandidatesSelection} // if candidate is empty we are disabling the dropdown but testing team and user don't know about it so commenting it out
              onDropdownOpen={fetchActiveCandidates
              }
            />
          )}
          {errors?.selected_candidate_id && (
            <ErrorMessage message={errors?.selected_candidate_id} />
          )}
        </div>
        {values.visibility_type !== "" &&
          values.visibility_type !==
            VACANCY_CANDIDATE_VISIBILITY.ANY_CANDIDATE && (
            <div className="d-flex flex-column gap-10">
              <Checkbox
                label={
                  values.visibility_type ===
                  VACANCY_CANDIDATE_VISIBILITY.PREFERRED_CANDIDATES
                    ? <>If there are no responses within a set timeframe, share this vacancy with <strong>all other candidates</strong>.</>
                    : <>If there is no response within a set timeframe, share this vacancy with <strong>all preferred candidates</strong>.</>
                }
                checked={!!values.response_timeframe_check}
                onChange={() =>
                  handleResponseTimeframeCheck(!values.response_timeframe_check)
                }
              />
              <div className="d-flex gap-3 align-items-center ps-33px">
                <div className="fs-13 text-black text-nowrap">
                  Set timeframe
                </div>
                <CustomSelect
                  options={responseTimeframes}
                  placeholder="Select..."
                  onSelect={(opt) =>
                    handleResponseTimeframeChange(opt.value as number)
                  }
                  defaultSelected={responseTimeframes.find(
                    (item) => item.value === values.response_timeframe
                  )}
                  enableFilter={false}
                  textOnly={true}
                />                
              </div>
              {errors?.response_timeframe && (
                <ErrorMessage message={errors?.response_timeframe} />
              )}
            </div>
          )}
        {values.visibility_type === VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE && (
          <div className="d-flex flex-column gap-10">
            <Checkbox
              label={<>If there are no responses within a set timeframe, share this vacancy with <strong>all other candidates</strong>.</>}
              checked={!!values.all_response_timeframe_check}
              onChange={() =>
                handleAllResponseTimeframeCheck(!values.all_response_timeframe_check)
              }
            />
            <div className="d-flex gap-3 align-items-center ps-33px">
              <div className="fs-13 text-black text-nowrap">Set timeframe</div>
              <CustomSelect
                options={filteredAllResponseTimeframes}
                placeholder="Select..."
                onSelect={(opt) =>
                  handleFieldChange("all_response_timeframe", opt.value)
                }
                value={filteredAllResponseTimeframes.find(
                  (item) => item.value === values.all_response_timeframe
                ) ?? null}
                enableFilter={false}
                textOnly={true}
              />
              </div>
              {errors?.all_response_timeframe && (
                <ErrorMessage message={errors?.all_response_timeframe} />
              )}            
          </div>
        )}
        {(values.visibility_type !==
          VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE ||
          (values.visibility_type ===
            VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE &&
            (values.response_timeframe_check ||
              values.all_response_timeframe_check))) && (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">
              How will the successful candidate be chosen?
            </div>
            <ToggleSelector
              options={CandidateSelectionOptions}
              onSelect={(opt) =>
                handleFieldChange("selection_method", opt.value)
              }
              defaultSelected={CandidateSelectionOptions.find(
                (item) => item.value === values.selection_method
              )}
            />
          </div>
        )}
        {isAdmin && (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">
              Indicate if a daily allowance needs to be added:
            </div>
            <div className="theme-check">
              <RadioInput
                label="Add a daily allowance"
                name="allowance"
                value="add"
                checked={values.allowance !== null && values.allowance > 0}
                onChange={() => {
                  handleFieldChange("allowance", 23);
                }}
                themeCheck={true}
              />
            </div>
            {values.allowance !== null && values.allowance > 0 && (
              <div className="d-flex flex-row gap-50 pl-50px">
                <div className="theme-check">
                  <RadioInput
                    label="$23.00"
                    name="allowance_amount"
                    value={23}
                    checked={values.allowance === 23}
                    onChange={() => handleFieldChange("allowance", 23)}
                    themeCheck={true}
                  />
                </div>
                <div className="theme-check">
                  <RadioInput
                    label="$38.00"
                    name="allowance_amount"
                    value={38}
                    checked={values.allowance === 38}
                    onChange={() => handleFieldChange("allowance", 38)}
                    themeCheck={true}
                  />
                </div>
                {errors?.allowance && (
                  <ErrorMessage message={errors?.allowance} />
                )}
              </div>
            )}
            <div className="theme-check">
              <RadioInput
                label="No allowance"
                name="allowance"
                value="none"
                checked={values.allowance === 0 || !values.allowance}
                onChange={() => {
                  handleFieldChange("allowance", 0);
                  // handleFieldChange("allowance_amount", null); // clear second level
                }}
                themeCheck={true}
              />
            </div>
            {errors?.allowance && <ErrorMessage message={errors?.allowance} />}
          </div>
        )}
        <div className="d-flex justify-content-end">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black mobile-w-100"
            isLoading={InProgress}
            onClick={onSubmit}
          >
            <RoundedTick color={InProgress ? "#aaaaaa" : "black"} />
            {!isEdit ? "Share With Candidates" : "Save Changes"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default VacancyFields;
